import React, { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, vs } from 'react-syntax-highlighter/dist/esm/styles/prism';
import './CodeBlock.css';

/**
 * CodeBlock component that renders code with syntax highlighting and a copy button
 * @param {Object} props
 * @param {boolean} props.inline - Whether this is inline code
 * @param {string} props.className - Class name containing language info
 * @param {React.ReactNode} props.children - Code content
 */
export default function CodeBlock({ inline, className, children, ...props }) {
    const [copied, setCopied] = useState(false);
    
    // Extract language from className (format: language-xxx)
    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : 'text';
    
    // Get code content as string
    const codeContent = String(children).replace(/\n$/, '');
    
    // Determine if this is simple text (few words, no code indicators)
    const isSimpleText = () => {
        // If a specific language is set (not 'text' or 'TEXT'), it's code
        const normalizedLang = language.toLowerCase();
        if (language && normalizedLang !== 'text') {
            return false;
        }
        
        // Check for code indicators
        const hasCodeIndicators = /[{}\[\]();=<>\/\\]|function|const|let|var|class|import|export|return/.test(codeContent);
        if (hasCodeIndicators) {
            return false;
        }
        
        // Count words (split by whitespace and filter empty strings)
        const words = codeContent.trim().split(/\s+/).filter(w => w.length > 0);
        const wordCount = words.length;
        
        // If it has multiple lines and decent length, it's likely code/content worth copying
        const lines = codeContent.split('\n').filter(l => l.trim().length > 0);
        if (lines.length > 2 || codeContent.length > 100) {
            return false;
        }
        
        // If it's just a few words (5 or less) and short, treat as simple text
        return wordCount <= 5 && codeContent.length <= 50;
    };
    
    const shouldShowCopyButton = !isSimpleText();
    
    // Handle copy to clipboard
    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(codeContent);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy code:', err);
        }
    };
    
    // Render inline code without syntax highlighting
    if (inline) {
        return (
            <code className={`inline-code ${className || ''}`} {...props}>
                {children}
            </code>
        );
    }
    
    // If language is explicitly TEXT (case-insensitive), check content length
    const normalizedLang = language.toLowerCase();
    if (normalizedLang === 'text') {
        // If text content is longer than 200 chars, render as code block
        if (codeContent.length > 200) {
            // Fall through to render as code block below
        } else {
            // Short text content - render as plain markdown text
            return codeContent;
        }
    }
    
    // If it's simple text, render as plain text without code block styling
    if (!shouldShowCopyButton) {
        return (
            <code className="simple-text-block" {...props}>
                {codeContent}
            </code>
        );
    }
    
    // Render block code with syntax highlighting and copy button
    return (
        <div className="code-block-wrapper">
            <div className="code-block-header">
                <span className="code-language">{language}</span>
                <button 
                    className={`copy-button ${copied ? 'copied' : ''}`}
                    onClick={handleCopy}
                    aria-label="Copy code"
                >
                    {copied ? (
                        <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            <span>Copied!</span>
                        </>
                    ) : (
                        <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                            </svg>
                            <span>Copy</span>
                        </>
                    )}
                </button>
            </div>
            <SyntaxHighlighter
                language={language}
                style={document.documentElement.classList.contains('dark-mode') || 
                       document.documentElement.classList.contains('theme-noctis') ||
                       document.documentElement.classList.contains('theme-github') ||
                       document.documentElement.classList.contains('theme-synthwave') ||
                       document.documentElement.classList.contains('theme-hackerx-green') ||
                       document.documentElement.classList.contains('theme-hackerx-ocean') ||
                       document.documentElement.classList.contains('theme-catppuccin-frappe') ||
                       document.documentElement.classList.contains('theme-catppuccin-macchiato') ||
                       document.documentElement.classList.contains('theme-catppuccin-mocha') ||
                       document.documentElement.classList.contains('theme-high-contrast') 
                       ? vscDarkPlus : vs}
                customStyle={{
                    margin: 0,
                    borderRadius: '0 0 6px 6px',
                    fontSize: '13px',
                    lineHeight: '1.5',
                }}
                wrapLines={true}
                wrapLongLines={true}
                {...props}
            >
                {codeContent}
            </SyntaxHighlighter>
        </div>
    );
}
